import { useNavigate } from 'react-router-dom'
import Header from '@/components/layout/Header'
import Footer from '@/components/layout/Footer'
import DesignCard from '@/components/dashboard/DesignCard'
import { useLang } from '@/hooks/useLang'
import { useAudio } from '@/hooks/useAudio'
import { MOCK_DESIGNS } from '@/data/mockDesigns'

const COPY = {
  fr: {
    tagline: 'Espace utilisateur',
    title: 'Mes Configurations',
    subtitle: 'Retrouvez, éditez et exportez vos projets robot.fric.link.',
    newBtn: 'Nouvelle configuration',
    statDesigns: 'Projets actifs',
    statExported: 'Cahiers exportés',
    statAvgScore: 'Faisabilité moy.',
    empty: 'Aucune configuration enregistrée.',
    emptyHint: 'Lancez le configurateur pour créer votre première conception.',
  },
  en: {
    tagline: 'User space',
    title: 'My Configurations',
    subtitle: 'Find, edit and export your robot.fric.link projects.',
    newBtn: 'New configuration',
    statDesigns: 'Active projects',
    statExported: 'Exported sheets',
    statAvgScore: 'Avg. feasibility',
    empty: 'No configuration saved yet.',
    emptyHint: 'Launch the configurator to create your first design.',
  },
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const { lang, toggle: toggleLang } = useLang()
  const { enabled: audioEnabled, playing: audioPlaying, toggle: toggleAudio } = useAudio(lang)
  const t = COPY[lang]

  const designs = MOCK_DESIGNS
  const exported = designs.filter((d) => d.status === 'exported').length
  const avgFeasibility = Math.round(
    designs.reduce((acc, d) => acc + d.scores.feasibility, 0) / (designs.length || 1)
  )

  return (
    <div className="min-h-screen bg-surface text-on-surface overflow-x-hidden selection:bg-primary-container selection:text-on-primary-container">
      <Header
        lang={lang}
        audioEnabled={audioEnabled}
        audioPlaying={audioPlaying}
        onToggleAudio={toggleAudio}
        onToggleLang={toggleLang}
      />

      <main className="relative pt-28 pb-24 px-6 md:px-12 max-w-7xl mx-auto">
        {/* Page header */}
        <div className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-px w-8 bg-primary-container" />
            <span className="label-technical">{t.tagline}</span>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <div>
              <h1 className="font-headline text-4xl md:text-5xl font-bold text-on-surface tracking-tight">
                {t.title}
              </h1>
              <p className="text-on-surface-variant font-body mt-3 max-w-lg">{t.subtitle}</p>
            </div>
            <button
              onClick={() => void navigate('/configurator')}
              className="btn-primary text-sm shrink-0"
            >
              <span className="material-symbols-outlined text-lg">add</span>
              {t.newBtn}
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-4 mb-12">
          {[
            { label: t.statDesigns, value: designs.length, icon: 'smart_toy', color: 'text-primary-container' },
            { label: t.statExported, value: exported, icon: 'picture_as_pdf', color: 'text-secondary' },
            { label: t.statAvgScore, value: `${avgFeasibility}%`, icon: 'verified', color: 'text-primary' },
          ].map((stat) => (
            <div key={stat.label} className="glass-panel rounded-xl p-6 border border-outline-variant/10">
              <div className="flex items-center gap-3 mb-2">
                <span className={`material-symbols-outlined text-xl ${stat.color}`}>{stat.icon}</span>
                <span className="label-technical">{stat.label}</span>
              </div>
              <div className={`font-headline text-3xl font-bold ${stat.color}`}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Designs grid */}
        {designs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4 text-center">
            <span className="material-symbols-outlined text-6xl text-on-surface-variant/20">smart_toy</span>
            <p className="font-headline text-xl text-on-surface-variant">{t.empty}</p>
            <p className="text-sm text-on-surface-variant/60 font-body">{t.emptyHint}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {designs.map((design) => (
              <DesignCard key={design.id} design={design} lang={lang} />
            ))}
          </div>
        )}
      </main>

      <Footer lang={lang} />
    </div>
  )
}
